import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  CalendarToday,
  Folder,
  Delete,
  Close,
  Star,
  StarBorder,
  Settings,
  CalendarMonth
} from '@mui/icons-material';
import { deleteSchedule, deleteScheduleUser, getSchedule, putSchedule, putScheduleUser } from '../../lib/connector/scheduleQueries';
import { getUserPool } from '../../lib/pools/pools';
import { v4 as uuidv4 } from 'uuid';
import { validateAuth } from '../../lib/Auth/Auth';
import { deletePosition, getPositions, putPosition } from '../../lib/connector/positionQueries';
import { useToast } from '../modals/ToastProvider/ToastProvider';
import { deleteShift, getShifts } from '../../lib/connector/shiftQueries';
import EditScheduleModal from '../modals/EditScheduleModal';
import { Schedule } from '../../pages/Dashboard/Dashboard';
import moment from 'moment';
import { lightTheme } from '../../contexts/ThemeManager/ThemeManager';
import { checkAccessLevel, updateUserAttribute } from '../../lib/authenticate';
import { getAuth, setAuth } from '../../contexts/authStore';
import UpgradeAccountModal from '../modals/UpgradeAccountModal/UpgradeAccountModal';
import CreateScheduleModal from '../modals/CreateScheduleModal/CreateScheduleModal';
import { SUBSCRIPTION_PLANS } from '../../pages/ProfileSettings/ProfileSettings';


const DRAWER_WIDTH = 320;
export const FREE_OVERRIDE = true; // Set to true to allow free users to create schedules (for testing purposes)

export const COLOR_OPTIONS = [
  { name: 'Stafflows Blue', value: '#0B74D1', light: '#E3F2FD' },
  { name: 'Purple', value: '#9C27B0', light: '#F3E5F5' },
  { name: 'Green', value: '#4CAF50', light: '#E8F5E9' },
  { name: 'Stafflows Orange', value: '#f57c00', light: '#FFF3E0' },
  { name: 'Red', value: '#F44336', light: '#FFEBEE' },
  { name: 'Teal', value: '#009688', light: '#E0F2F1' },
  { name: 'Pink', value: '#E91E63', light: '#FCE4EC' },
  { name: 'Indigo', value: '#3F51B5', light: '#E8EAF6' },
  { name: 'Cyan', value: '#00BCD4', light: '#E0F7FA' },
  { name: 'Amber', value: '#FFC107', light: '#FFF8E1' },
  { name: 'Deep Purple', value: '#673AB7', light: '#EDE7F6' },
  { name: 'Lime', value: '#CDDC39', light: '#F9FBE7' },
];

interface DashboardSidebarProps {
  open: boolean;
  onToggle: () => void;
  setSelectedSchedule: (schedule: Schedule) => void;
  selectedSchedule: Schedule | null;
  userData: Record<string, any>;
  accessLevel: string | null;
  shifts: any[];
  onOpenHelp?: (section?: string) => void;
}

export default function DashboardSidebar({ open, onToggle, setSelectedSchedule, selectedSchedule, userData, accessLevel, shifts, onOpenHelp }: DashboardSidebarProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [schedules, setSchedules] = useState<any[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<Schedule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showOwnedOnly, setShowOwnedOnly] = useState(false);

  const currentUsername = getUserPool()?.getCurrentUser()?.getUsername() ?? '';

  React.useEffect(() => {
    if (validateAuth()) {
      const fetchSchedules = async () => {
        let schedules: any[] = [];
        const scheduleIds = getAuth().user_type?.split(",") || [];

        for (const ownerScheduleId of scheduleIds) {
          const response = await getSchedule(ownerScheduleId.split("#")[0], ownerScheduleId.split("#")[1]);
          if (response && response.length > 0) {
            schedules.push(response[0]);
          }
        }

        setSchedules(schedules);
        setSelectedSchedule(userData.primarySchedule ? schedules.find((schedule: { scheduleId: any; }) => schedule.scheduleId === userData.primarySchedule)
          : schedules.length > 0 ? schedules[0] : null);
      };

      fetchSchedules();
    }
  }, [])

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, schedule: Schedule) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setActiveMenu(schedule);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveMenu(null);
  };

  const handleCreateSchedule = (name: string, frequency: string, startDate: string) => {
    try {
      let activeScheduleDate: string;
      switch (frequency) {
        case 'bi-weekly': activeScheduleDate = moment(startDate).add(13, 'days').format('YYYY-MM-DD'); break;
        case 'monthly': activeScheduleDate = moment(startDate).endOf('month').format('YYYY-MM-DD'); break;
        default: activeScheduleDate = moment(startDate).add(6, 'days').format('YYYY-MM-DD');
      }

      const newSchedule = {
        scheduleId: uuidv4(),
        name,
        favorite: false,
        color: COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)].value,
        shiftsCount: 0,
        employees: [],
        link: '',
        userId: getUserPool()!.getCurrentUser()!.getUsername(),
        lastActiveScheduleDate: startDate,
        activeScheduleFrequency: frequency,
        allowSwapWithoutApproval: false,
        allowPickUpWithoutApproval: false,
        viewOtherStaff: false,
        availabilityScheduleRequiredTime: 1,
        activeScheduleDate,
        lastSubmitted: moment().format('YYYY-MM-DD'),
        primarySchedule: false
      };

      putSchedule(
        getUserPool()!.getCurrentUser()!.getUsername(),
        newSchedule.scheduleId,
        newSchedule.name,
        newSchedule.color,
        '',
        [],
        false,
        false,
        false,
        false,
        newSchedule.activeScheduleFrequency,
        newSchedule.lastActiveScheduleDate,
        newSchedule.availabilityScheduleRequiredTime,
        newSchedule.activeScheduleDate,
        newSchedule.lastSubmitted,
      );
      const updatedUserType = getAuth().user_type && getAuth().user_type !== '' ? getAuth().user_type + ',' : '';
      updateUserAttribute('custom:user_type',
        updatedUserType + getUserPool()!.getCurrentUser()!.getUsername() + '#' + newSchedule.scheduleId).then(() => {
          updateUserAttribute('custom:role',
            newSchedule.scheduleId + "#admin").then(() => {
              setAuth({ user_type: updatedUserType + getUserPool()!.getCurrentUser()!.getUsername() + '#' + newSchedule.scheduleId, role: newSchedule.scheduleId + "#admin" } as any);
              putPosition(newSchedule.scheduleId, uuidv4(), 'Admin', [getUserPool()!.getCurrentUser()!.getUsername()!]);
              putScheduleUser(getUserPool()!.getCurrentUser()!.getUsername(), newSchedule.scheduleId + '#' + getUserPool()!.getCurrentUser()!.getUsername(), userData.name, 'admin');
            })
        })



      setSchedules([...schedules, newSchedule]);
      setSelectedSchedule(newSchedule);
      toast.showSuccess(name + ' - created successfully.');
    } catch (error: any) {
      toast.showError('Error creating schedule. Please try again.', error.message);
    }
  };

  const handleUpdateSchedule = async (
    scheduleId: string,
    name: string,
    color: string,
    favorite: boolean,
    activeScheduleFrequency: string,
    availabilityScheduleRequiredTime: number,
    settings: {
      allowSwapWithoutApproval: boolean,
      allowPickUpWithoutApproval: boolean,
      showOtherStaff: boolean,
    }
  ) => {
    await putSchedule(activeMenu!.userId, activeMenu!.scheduleId, name, color,
      activeMenu!.link, activeMenu!.employees, favorite, settings.allowSwapWithoutApproval,
      settings.allowPickUpWithoutApproval, settings.showOtherStaff, activeScheduleFrequency,
      activeMenu!.lastActiveScheduleDate, availabilityScheduleRequiredTime, activeMenu!.activeScheduleDate,
      activeMenu!.lastSubmitted);

    // Refresh schedules list
    setSchedules(schedules.map(s =>
      s.scheduleId === scheduleId ? { ...s, name, color, favorite } : s
    ));



  };

  const handleToggleFavorite = (scheduleId: string) => {
    const schedule = schedules.find(s => s.scheduleId === scheduleId);
    if (!schedule) return;
    putSchedule(activeMenu!.userId, schedule.scheduleId, schedule.name, schedule.color,
      schedule.link, schedule.employees, !schedule.favorite, schedule.allowSwapWithoutApproval,
      schedule.allowPickUpWithoutApproval, schedule.viewOtherStaff, schedule.activeScheduleFrequency,
      schedule.lastActiveScheduleDate, schedule.availabilityScheduleRequiredTime, schedule.activeScheduleDate,
      schedule.lastSubmitted);
    setSchedules(schedules.map(s =>
      s.scheduleId === scheduleId ? { ...s, favorite: !s.favorite } : s
    ));
    handleMenuClose();
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    try {
      let canDelete = true
      schedules.map((s) => {
        if (s.scheduleId === scheduleId) {
          if (s.employees && s.employees.length > 0) {
            toast.showError('Cannot delete schedule with employees. Please remove all employees before deleting the schedule.');
            canDelete = false;
            return;
          }
        }
      })
      if (!canDelete) { return; }
      deleteSchedule(activeMenu!.userId, scheduleId).then(() => {
        getPositions(scheduleId).then((positions) => {
          positions.map((position: any) => {
            deletePosition(scheduleId, position.position);
          })
        })
        getShifts(scheduleId).then((shifts) => {
          shifts.map((shift: any) => {
            deleteShift(scheduleId, shift.shiftId);
          })
        });
        const schedule = schedules.find(s => s.scheduleId === scheduleId);
        if (schedule && schedule.employees) {
          schedule.employees.map((employee: string) => {
            deleteScheduleUser(employee, scheduleId + "#" + schedule.userId);
          });
        }
      })
      setSchedules(schedules.filter(s => s.scheduleId !== scheduleId));
      if (selectedSchedule?.scheduleId === scheduleId) {
        setSelectedSchedule(schedules[0]);
      }
      handleMenuClose();
      toast.showSuccess('Schedule deleted successfully.');
    } catch (error: any) {
      toast.showError('Error deleting schedule. Please try again.', error.message);
    }
  };

  const filteredSchedules = schedules
    .filter(schedule => schedule.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(schedule => !showOwnedOnly || schedule.userId === currentUsername);

  const favoriteSchedules = filteredSchedules.filter(s => s.favorite);
  const otherSchedules = filteredSchedules.filter(s => !s.favorite);

  return (
    <>
      {/* Sidebar Toggle Button */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: 'fixed',
          left: open ? DRAWER_WIDTH + 16 : 16,
          top: { xs: 60, sm: 80, md: 80, lg: 80 },
          zIndex: 1300,
          bgcolor: 'info.main',
          color: lightTheme.palette.background.paper,
          boxShadow: 2,
          transition: 'left 0.3s',
          '&:hover': { bgcolor: 'info.light' }
        }}
      >
        {open ? <Close /> : <CalendarMonth />}
      </IconButton>

      {/* Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          },
        }}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Schedules
              </Typography>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search schedules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label="My Schedules"
              size="small"
              clickable
              onClick={() => setShowOwnedOnly(!showOwnedOnly)}
              color={showOwnedOnly ? 'primary' : 'default'}
              variant={showOwnedOnly ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          </Box>
        </Box>

        {/* Create New Schedule Button */}
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              if (!getAuth().user_type) {
                if (FREE_OVERRIDE === true) {
                  setShowCreateDialog(true)
                  return;
                }
                if (getAuth().access_level === "free") {
                  setShowUpgradeModal(true);
                } else {
                  setShowCreateDialog(true)
                }
              } else {
                const ownedSchedules = getAuth().user_type?.split(",").filter((item) => item.includes(getUserPool()!.getCurrentUser()!.getUsername()))
                if (ownedSchedules && ownedSchedules.length === 0) {
                  if (getAuth().access_level === "free") {
                    setShowUpgradeModal(true);
                    return;
                  }
                } else if (ownedSchedules && ownedSchedules.length >= SUBSCRIPTION_PLANS[1].maxSchedules &&
                  ownedSchedules.length < SUBSCRIPTION_PLANS[2].maxSchedules) {
                  if (getAuth().access_level !== "pro" && getAuth().access_level !== "business") {
                    setShowUpgradeModal(true);
                    return;
                  }
                } else if (ownedSchedules && ownedSchedules.length >= SUBSCRIPTION_PLANS[2].maxSchedules) {
                  if (getAuth().access_level !== "business") {
                    setShowUpgradeModal(true);
                    return;
                  }
                }
                setShowCreateDialog(true)
              }
            }}
          >
            New Schedule
          </Button>
          {(() => {
            const ownedCount = (getAuth().user_type?.split(",") ?? []).filter((item) =>
              item.includes(getUserPool()!.getCurrentUser()!.getUsername())
            ).length;
            const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === (getAuth().access_level ?? 'free')) ?? SUBSCRIPTION_PLANS[0];
            const maxSchedules = FREE_OVERRIDE ? -1 : currentPlan.maxSchedules;
            const isUnlimited = maxSchedules === -1;
            const pct = isUnlimited ? 0 : Math.min(100, (ownedCount / maxSchedules) * 100);
            const atLimit = !isUnlimited && ownedCount >= maxSchedules;
            return (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Plan allowed schedules
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={atLimit ? 'error.main' : 'text.secondary'}
                  >
                    {ownedCount}{isUnlimited ? '' : ` / ${maxSchedules}`}
                    {isUnlimited && <Typography component="span" variant="caption" color="text.disabled"> (unlimited)</Typography>}
                  </Typography>
                </Box>
                {!isUnlimited && (
                  <Tooltip title={atLimit ? `Upgrade to create more schedules` : `${maxSchedules - ownedCount} remaining on ${currentPlan.name} plan`}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: atLimit ? 'error.main' : 'primary.main',
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* Schedule Lists */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {/* Favorites */}
          {favoriteSchedules.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Favorites
              </Typography>
              <List disablePadding>
                {favoriteSchedules.map((schedule) => (
                  <ListItem
                    key={schedule.scheduleId}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, schedule)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      selected={selectedSchedule?.scheduleId === schedule.scheduleId}
                      onClick={() => setSelectedSchedule(schedule)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.50',
                          '&:hover': {
                            bgcolor: 'primary.100',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: schedule.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Folder sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={schedule.name}
                        secondary={`${schedule.employees?.length} staff`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                      <Star sx={{ fontSize: 16, color: 'warning.main', mr: 1 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* All Schedules */}
          {otherSchedules.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
              >
                All Schedules
              </Typography>
              <List disablePadding>
                {otherSchedules.map((schedule) => (
                  <ListItem
                    key={schedule.scheduleId}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, schedule)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      selected={selectedSchedule?.scheduleId === schedule.scheduleId}
                      onClick={() => {
                        setSelectedSchedule(schedule)
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.50',
                          '&:hover': {
                            bgcolor: 'primary.100',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: schedule.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Folder sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={schedule.name}
                        secondary={`${schedule.employees?.length} staff`}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Empty State */}
          {filteredSchedules.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <CalendarToday sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No schedules found' : 'No schedules yet'}
              </Typography>
              {!searchQuery && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowCreateDialog(true)}
                  sx={{ mt: 2 }}
                >
                  Create your first schedule
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Current Selection Info */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Currently Viewing
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: selectedSchedule?.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Folder sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedSchedule?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedSchedule?.employees?.length} staff | {shifts.filter(shift => shift.scheduleId === selectedSchedule?.scheduleId && shift.isRecurring).length} regular shifts
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        disableEnforceFocus
      >
        <MenuItem onClick={() => activeMenu && handleToggleFavorite(activeMenu.scheduleId)}>
          <ListItemIcon>
            {activeMenu?.favorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {activeMenu?.favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </ListItemText>
        </MenuItem>
        {checkAccessLevel(accessLevel) ? <MenuItem onClick={() => setShowEditScheduleModal(true)}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem> : <></>}
        {userData.accountType === "owner" ? <><Divider /><MenuItem
          onClick={() => { if (activeMenu) handleDeleteSchedule(activeMenu.scheduleId); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Schedule</ListItemText>
        </MenuItem></> : <></>}
      </Menu>

      <CreateScheduleModal
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreateSchedule={handleCreateSchedule}
        onOpenHelp={onOpenHelp}
      />

      <EditScheduleModal
        open={showEditScheduleModal}
        onClose={() => setShowEditScheduleModal(false)}
        schedule={activeMenu}
        onSave={handleUpdateSchedule}
        existingSchedules={schedules}
      />

      <UpgradeAccountModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => { navigate('/profile?tab=billing'); }}
        featureName="More Schedules"
        currentPlan={({ free: 'Free', basic: 'Basic', pro: 'Pro', business: 'Business' })[getAuth().access_level ?? 'free'] ?? 'Free'}
      />
    </>
  );
}